import { useState } from "react";
import { Plus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface BookingNoteFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Mandatory note (elevated user booking for themselves). Renders expanded with a `*`. */
  required?: boolean;
  label?: string;
  placeholder?: string;
  className?: string;
}

/**
 * A booking note input that stays out of the way. Optional notes render as a compact "Add a note"
 * toggle that expands to a textarea (and stays expanded once opened or once it has content).
 * Mandatory notes (staff self-bookings) render expanded with a required marker. The captured text is
 * persisted by the caller as a booking comment.
 */
const BookingNoteField = ({
  value,
  onChange,
  required = false,
  label = "Booking note",
  placeholder = "Add a note for this booking (e.g. why the room is held, or a reminder for billing)",
  className,
}: BookingNoteFieldProps) => {
  const [expanded, setExpanded] = useState(false);
  const open = required || expanded || value.trim().length > 0;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-expanded={false}
        data-testid="booking-note-toggle"
        className={cn(
          "inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded",
          className,
        )}
      >
        <Plus className="w-4 h-4" /> Add a note (optional)
      </button>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1", className)} data-testid="booking-note-field">
      <label htmlFor="booking-note-input" className="text-sm font-medium">
        {label}{" "}
        {required ? <span className="text-red-600">*</span> : <span className="text-gray-400">(optional)</span>}
      </label>
      <Textarea
        id="booking-note-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-required={required}
        className="h-20 bg-white"
      />
    </div>
  );
};

export default BookingNoteField;
